import { useEffect, type FC } from "react";
import { useQuery } from "@apollo/client";
import { Button, LinearProgress, styled } from "@mui/material";
import {
  NextContentQuery,
  NextPlaylistContentQuery,
} from "@/entities/contents";
import { Link, replaceRouteDialog } from "@/shared/components/RouteDialog";
import { useTranslation } from "@/shared/hooks";
import type {
  NextContentQueryType,
  NextPlaylistContentQueryType,
} from "@/types/graphql/site.generated";

// За сколько секунд до конца ролика показывать кнопку перехода (референс).
const TIMEOUT = 10;

const Label = styled("span")({
  zIndex: 1,
});

const StyledLinearProgress = styled(LinearProgress)({
  position: "absolute",
  left: 0,
  right: 0,
  bottom: 0,
  top: 0,
  height: "auto",
  zIndex: 0,
});

// Позиционирование вынесено в обёртку: styled(Button) с кастомным пропом
// теряет типизацию полиморфного `component`, а Button внутри остаётся
// обычным MUI-компонентом с component={Link}.
const Positioner = styled("div", {
  shouldForwardProp: (prop) => prop !== "visible",
})<{ visible?: boolean }>(({ theme, visible }) => ({
  zIndex: 2,
  position: "absolute",
  bottom: visible ? 88 : theme.spacing(1),
  right: theme.spacing(1),
  ["@media (min-width: 375px)"]: {
    bottom: visible ? 96 : theme.spacing(2),
    right: theme.spacing(2),
  },
  [theme.breakpoints.up("md")]: {
    bottom: visible ? 96 : theme.spacing(3),
    right: theme.spacing(3),
  },
}));

// Порт референсного NextPlaylistContentButton (ветка featured-контента):
// появляется за TIMEOUT секунд до конца с прогрессом заполнения, по концу
// ролика делает replace-переход к следующему видео (back после автоперехода
// ведёт на фоновую страницу, не на предыдущее видео). Клик по кнопке — как
// обычная ссылка (push-переход диалога).
export const NextContentButton: FC<{
  contentId: string;
  /** Плейлист-контекст: следующий берётся из порядка коллекции
   * (nextPlaylistContent), ссылка сохраняет ?collectionId= */
  collectionId?: string;
  currentTime: number;
  duration: number;
  ended: boolean;
  visible: boolean;
}> = ({ contentId, collectionId, currentTime, duration, ended, visible }) => {
  const { t } = useTranslation();
  const timeLeft = duration - currentTime;
  // Запрос уходит, только когда до конца осталось меньше TIMEOUT секунд.
  const skip = !duration || timeLeft > TIMEOUT;

  const { data } = useQuery<NextContentQueryType>(NextContentQuery, {
    skip: skip || Boolean(collectionId),
    variables: { contentId },
  });
  const { data: playlistData } = useQuery<NextPlaylistContentQueryType>(
    NextPlaylistContentQuery,
    {
      skip: skip || !collectionId,
      variables: { contentId, collectionId },
    },
  );

  const nextPlaylistPath = playlistData?.nextPlaylistContent?.path
    ? `${playlistData.nextPlaylistContent.path}?collectionId=${collectionId}`
    : null;
  const nextPath = collectionId
    ? nextPlaylistPath
    : (data?.nextContent?.path ?? null);

  // `ended` не переживает смену контента: useVideoEngine сбрасывает
  // состояние синхронно при смене src, поэтому конец прошлого ролика не
  // может сработать со свежим nextPath следующего.
  useEffect(() => {
    if (!ended || !nextPath) return;
    replaceRouteDialog(nextPath);
  }, [ended, nextPath]);

  if (!duration) return null;
  if (skip) return null;
  if (!nextPath) return null;

  const progress = Math.min(100, Math.max(0, 100 - timeLeft * TIMEOUT));

  return (
    <Positioner visible={visible}>
      <Button
        variant="contained"
        component={Link}
        href={nextPath}
        sx={{ overflow: "hidden", position: "relative" }}
      >
        <StyledLinearProgress variant="determinate" value={progress} />
        <Label>{t("video.next")}</Label>
      </Button>
    </Positioner>
  );
};
